/**
 * Contratto della edge function `get-flight-information`.
 *
 * Il DTO e' neutro rispetto al provider (oggi AirLabs): la edge function normalizza
 * la risposta, il frontend non conosce ne' il provider ne' il suo formato.
 */

/** Scalo — partenza o arrivo — cosi' come lo restituisce la edge function. */
export interface FlightLookupEndpoint {
  airportName: string;
  airportIata: string;
  city: string;
  terminal?: string | null;
  gate?: string | null;
  /**
   * ISO 8601 **senza suffisso di fuso** (`2026-09-23T07:05:00`): ora locale dello
   * scalo, cioe' un orario da parete e non un istante. Va letto come stringa, non
   * passato a `new Date()` — vedi `splitScheduledTime`.
   */
  scheduledTime: string;
  estimatedTime?: string | null;
}

export interface FlightLookupResponse {
  airline: {
    iata: string;
    icao?: string | null;
    name: string;
  };
  flight: {
    iata: string;
    number: string;
    status: string;
  };
  departure: FlightLookupEndpoint;
  arrival: FlightLookupEndpoint;
  aircraft?: {
    model?: string | null;
    registration?: string | null;
  } | null;
}

/**
 * Codici di errore del contratto. Il `message` che li accompagna e' a uso diagnostico:
 * all'utente si mostra sempre e solo la stringa i18n corrispondente.
 */
export type FlightLookupErrorCode =
  | 'invalid_json'
  | 'invalid_flight_code'
  | 'flight_not_found'
  | 'rate_limited'
  | 'provider_unavailable'
  | 'proxy_unavailable'
  | 'missing_configuration'
  | 'unknown';

/**
 * Errori che non hanno senso ritentare: dipendono da cosa ha digitato il TD,
 * non da un guasto temporaneo. L'hook ci spegne il retry di React Query.
 */
export const FLIGHT_LOOKUP_FUNCTIONAL_ERRORS: readonly FlightLookupErrorCode[] = [
  'invalid_json',
  'invalid_flight_code',
  'flight_not_found',
] as const;

/** Regex del contratto, applicata al codice gia' normalizzato (uppercase, senza separatori). */
export const FLIGHT_CODE_PATTERN = /^([A-Z0-9]{2})([0-9]{1,4})$/;

/**
 * Risultato del lookup dopo la fusione con la riga `airlines`: e' questo che la
 * preview mostra e che alimenta la tratta. La compagnia e' gia' risolta — se la
 * conosciamo a DB vincono nome, logo e colore nostri, altrimenti resta il provider.
 */
export interface ResolvedFlightLookup {
  flight: FlightLookupResponse;
  airline: {
    iata: string;
    name: string;
    logoUrl: string | null;
    color: string | null;
    /** true quando la compagnia e' stata trovata nella tabella `airlines`. */
    fromDatabase: boolean;
  };
}
